/**
 * Client <-> server protocol — slice 2 (lobby + game rooms).
 *
 * The single source of truth for Colyseus room names, message channel names,
 * and message payload shapes. **Both client and server import from here** so
 * the wire format can never drift (MASTER_PROMPT §4 rule 2, §6.2).
 *
 * Conventions:
 *  - Message *names* (the Colyseus channel string) are named constants grouped
 *    by direction (`ClientMessage` / `ServerMessage`) so call sites read as
 *    `room.send(ClientMessage.SetReady, ...)` and
 *    `room.onMessage(ServerMessage.Error, ...)`.
 *  - Each message name has a matching `*Payload` type below.
 *  - Authoritative *state* (the roster, ready flags, host, status) is NOT sent
 *    as messages — it lives in the Colyseus `Schema` and is replicated by the
 *    framework. The state-shape contract for that Schema is in `lobby.ts`.
 *    Messages here are the discrete *requests* (client→server) and one-off
 *    *events/acks* (server→client) that sit alongside the synced state.
 *
 * Server-authority + room-lifecycle rationale:
 * `docs/design/contracts/slice2-protocol.md`.
 */
// ---------------------------------------------------------------------------
// Room names + join-code rules
// ---------------------------------------------------------------------------
/** Colyseus room name for the matchmaking/lobby room (lists rooms, hosts create). */
export const LOBBY_ROOM_NAME = 'lobby';
/** Colyseus room name for an in-progress game/room instance (one per room code). */
export const GAME_ROOM_NAME = 'game';
/**
 * Number of digits in a room join code. **4 NUMERIC digits** (owner ruling,
 * 2026-06-01) — matches the prototype's Join-by-Code modal. Codes are strings
 * of exactly this many characters drawn from {@link ROOM_CODE_ALPHABET}.
 */
export const ROOM_CODE_LENGTH = 4;
/** Allowed characters in a room code — digits only (numeric, no letters). */
export const ROOM_CODE_ALPHABET = '0123456789';
/**
 * Validate a room code: exactly {@link ROOM_CODE_LENGTH} characters, all from
 * {@link ROOM_CODE_ALPHABET}. Used both client-side (modal input gate) and
 * server-side (authoritative check before join). Leading zeros are valid
 * (codes are strings, never numbers — e.g. `"0042"`).
 */
export function isValidRoomCode(code) {
    if (code.length !== ROOM_CODE_LENGTH)
        return false;
    for (const ch of code) {
        if (!ROOM_CODE_ALPHABET.includes(ch))
            return false;
    }
    return true;
}
// ---------------------------------------------------------------------------
// Message names (the Colyseus channel strings)
// ---------------------------------------------------------------------------
/** Messages the client sends to the server. */
export const ClientMessage = {
    // Lobby room (matchmaking) ------------------------------------------------
    /** Ask the lobby to (re)send the current room list. */
    ListRooms: 'lobby:list-rooms',
    /** Host: create a new game room; server replies with `RoomCreated`. */
    CreateRoom: 'lobby:create-room',
    /** Join an existing room by its code; server replies `JoinAccepted`/`Error`. */
    JoinByCode: 'lobby:join-by-code',
    // Game room (a specific room instance) ------------------------------------
    /** Set this player's ready flag in the room. */
    SetReady: 'room:set-ready',
    /** Host only: change the room's mode (co-op / pvp). */
    SetMode: 'room:set-mode',
    /** Host only: change the selected stage/level for the room. */
    SetStage: 'room:set-stage',
    /** Host only: start the game once everyone present is ready. */
    StartGame: 'room:start-game',
    /** Send a lobby chat line (broadcast to the room as `ChatPosted`). */
    Chat: 'room:chat',
    /** Fire a transient emote (broadcast to the room as `EmotePosted`). */
    Emote: 'room:emote',
    /** Leave the room cleanly (also covered by disconnect, but explicit on Back). */
    Leave: 'room:leave',
    // In-stage gameplay (slice 3) --------------------------------------------
    /**
     * Movement **intent** for this player's Pon Pon (move dir + jump). The client
     * NEVER sends a position — only intent; the server simulates + decides the
     * outcome (MASTER_PROMPT §4 rule 3). Position rides the synced Schema, not a
     * message. Sent on input change (and may be re-sent each tick for robustness).
     */
    Input: 'game:input',
    // In-stage Absorb — the ONE co-op verb (crate carry + teammate chain) --------
    /**
     * **Absorb** **intent**: "absorb the nearest absorbable now." No payload — the
     * **server** decides the target from what is in range (there is no "grab"; the
     * verb is Absorb, absorb-contract §2). Picks the nearest in-range **crate XOR
     * teammate** (facing-biased), then connects + latches it:
     *  - **crate**: the nearest free crate within `level.ts#ABSORB_RANGE`
     *    (= `BLOCK_GRAB_RANGE`), not already held → carry it (set `PonState.grabbing`
     *    + `BlockState.grabbedBy`); dragged flush on the carrier's facing side.
     *  - **teammate**: the nearest valid teammate within `level.ts#ABSORB_RANGE`
     *    (= `CHAIN_LINK_RANGE`) → **chain-append** them (set `PonState.chainNext` /
     *    the absorbed teammate's `chainPrev`); the absorbed Pon is **pinned** into the
     *    formation slot. Every teammate-Absorb is a chain-append (the slice-5
     *    `linkedTo` tether is retired, absorb-contract §5).
     *
     * **One Absorb target per Pon (crate XOR teammate):** if the sender is already
     * carrying *anything* (`grabbing` set OR `chainNext` set) it is a no-op
     * (first-claim-wins). When both a crate and a teammate are in range, the server
     * picks the **nearest** (facing-side biased); on a tie, **crate precedence**. The
     * client never names the target. Sent on the Absorb key edge (press), not held.
     */
    Grab: 'game:grab',
    /**
     * Release **intent** ("tap Absorb again to drop"): "drop whatever I'm carrying."
     * No payload — the server clears whichever Absorb target the sender holds:
     *  - a carried **crate** → `grabbing`/`grabbedBy` cleared; the crate becomes a
     *    free body where it sits (obeys gravity).
     *  - an absorbed **teammate** → the chain **splits at this member**
     *    (`chainPrev`/`chainNext` cleared at the boundary); the freed downstream
     *    sub-chain re-evaluates its anchor (unanchored ⇒ unpinned ⇒ falls/respawns).
     * No-op if this player holds nothing. Sent on the Release key edge. (Absorb/Release
     * are an explicit pair rather than one toggle so the wire intent is unambiguous;
     * the server still enforces all outcomes.)
     */
    Release: 'game:release',
};
/** Messages the server sends to the client. */
export const ServerMessage = {
    // Lobby room --------------------------------------------------------------
    /** Full snapshot of joinable rooms (also pushed on change while subscribed). */
    RoomList: 'lobby:room-list',
    /** Ack for `CreateRoom`: the code/name to re-join the new game room with. */
    RoomCreated: 'lobby:room-created',
    /** Ack for `JoinByCode`: the room exists and you may re-join the game room. */
    JoinAccepted: 'lobby:join-accepted',
    // Game room ---------------------------------------------------------------
    /** Server told the room to begin: clients transition lobby → stage/game. */
    GameStarting: 'room:game-starting',
    /** A chat line was posted in the room (broadcast). */
    ChatPosted: 'room:chat-posted',
    /** A player fired an emote (broadcast, transient — not stored in state). */
    EmotePosted: 'room:emote-posted',
    // In-stage gameplay (slice 3) --------------------------------------------
    /**
     * Authoritative Stage Clear: every present player reached the door, the door
     * opened, the stage is cleared. A one-off trigger event that rides ALONGSIDE
     * the synced `GameState.cleared` flag — both exist (the flag is the truth, the
     * event is the clean transition trigger), mirroring slice-2's `GameStarting`.
     */
    StageClear: 'game:stage-clear',
    // Either room -------------------------------------------------------------
    /** A request was rejected (bad code, room full, not host, etc.). */
    Error: 'error',
};
//# sourceMappingURL=protocol.js.map